/*
  The date utils mainly uses date-fns for date formatting and transformations
  https://date-fns.org/docs/Getting-Started
 */

import {
  format as formatFns,
  subDays,
  isSameDay,
  isSameYear,
  startOfWeek,
  endOfWeek,
  getWeek,
  subYears,
} from "date-fns";
import { fr, it, es, ro, enGB, enUS, de, ar, ja } from "date-fns/locale";
import store from "@/store";
import { getLocaleFrom } from "@/utils/locales";

// Japanese date format reference:
// https://en.wikipedia.org/wiki/Date_and_time_notation_in_Japan
// N.B.: the current version of date-fns supports badly year and day kanjis so we add them
const yyyyMMdd = "yyyy-MM-dd";
const DMMMMYYYY = "d MMMM yyyy";
const jaDMMMMYYYY = "yyyy年MMMMd日";
const DDMMMYYYY = "dd MMM yyyy";
const jaDDMMMYYYY = "yyyy/MM/dd";
const DDMMM = "dd MMM";
const jaDDMMM = "MMMdd日";
const DMMMM = "d MMMM";
const jaDMMMM = "MMMMd日";
const DMMM = "d MMM";
const jaDMMM = "MMMd日";
const D = "d";
const jaD = "d日";
const EEEEDMMMM = "EEEE, d MMMM"; // Monday, 1
const jaEEEEDMMMM = "MMMMd日 EEEE";
const EEEEDMMMMYYYY = "EEEE, d MMMM yyyy"; // Monday, 1 january 1900
const jaEEEEDMMMMYYYY = "yyyy年MMMMd日 EEEE";
const MMMMYYYY = "MMMM yyyy";
const jaMMMMYYYY = "yyyy年MMMM";
const DDMMMYYYYHHmm = "dd MMM yyyy HH:mm";
const jaDDMMMYYYYHHmm = "yyyy年MMMdd日 HH:mm";
const DDMMMYYYYHHmmA = "dd MMM yyyy hh:mm a";
const jaDDMMMYYYYHHmmA = "yyyy年MMMdd日 hh:mm a";
const DDMMMYYYYHHmmssSSS = "dd MMM yyyy HH:mm:ss.SSS";
const jaDDMMMYYYYHHmmssSSS = "yyyy年MMMdd日 HH:mm:ss.SSS";
const DDMMMYYYYHHmmssSSSA = "dd MMM yyyy hh:mm:ss.SSS a";
const jaDDMMMYYYYHHmmssSSSA = "yyyy年MMMdd日 hh:mm:ss.SSS a";
const DDMMMYYYYHHMMss = "dd MMM yyyy HH:mm:ss";
const jaDDMMMYYYYHHMMss = "yyyy年MMMdd日 HH:mm:ss";
const DDMMMYYYYHHMMssA = "dd MMM yyyy hh:mm:ss a";
const jaDDMMMYYYYHHMMssA = "yyyy年MMMdd日 hh:mm:ss a";
const Hmm = "H:mm";
const HmmA = "h:mm a";
const HHmm = "HH:mm";
const HHmmA = "hh:mm a";
const MMDD = "MM dd";
const jaMMDD = "MMdd日";

/**
 * date-fns Locale object used for different time/date localization purposes.
 */
const locales = {
  "en-GB": enGB,
  "en-US": enUS,
  fr: fr,
  it: it,
  es: es,
  ro: ro,
  de: de,
  ar: ar,
  ja: ja,
};

function getDefaultBirthDate() {
  return subYears(new Date(), 70);
}

function getJapaneseDateFormat(format) {
  const japaneseMapping = Object.freeze({
    [DMMMMYYYY]: jaDMMMMYYYY,
    [DDMMMYYYY]: jaDDMMMYYYY,
    [DDMMM]: jaDDMMM,
    [DMMMM]: jaDMMMM,
    [DMMM]: jaDMMM,
    [EEEEDMMMM]: jaEEEEDMMMM,
    [EEEEDMMMMYYYY]: jaEEEEDMMMMYYYY,
    [MMMMYYYY]: jaMMMMYYYY,
    [DDMMMYYYYHHmm]: jaDDMMMYYYYHHmm,
    [DDMMMYYYYHHmmA]: jaDDMMMYYYYHHmmA,
    [DDMMMYYYYHHmmssSSS]: jaDDMMMYYYYHHmmssSSS,
    [DDMMMYYYYHHmmssSSSA]: jaDDMMMYYYYHHmmssSSSA,
    [DDMMMYYYYHHMMss]: jaDDMMMYYYYHHMMss,
    [DDMMMYYYYHHMMssA]: jaDDMMMYYYYHHMMssA,
    [MMDD]: jaMMDD,
    [D]: jaD,
  });

  // Return translated format if any, and current format otherwise.
  return japaneseMapping[format] || format;
}

function format(value, format) {
  const currentLanguage = store.getters["Language/language"];
  // Check if the date format needs japanese translation
  if (currentLanguage === "ja") {
    format = getJapaneseDateFormat(format);
  }

  // check if value is a date
  if (value instanceof Date) {
    return formatFns(value, format, {
      locale: locales[currentLanguage],
    });
  }

  // If value is null or undefined return empty string
  if (!value) {
    return "";
  }

  // if it is not a date format it to date
  if (!(value instanceof Date)) {
    value = new Date(value);
  }

  return formatFns(value, format, {
    locale: locales[currentLanguage],
  });
}

function use12HourFormat() {
  const locale = locales[getLocaleFrom(store.getters["Language/language"])];
  // date-fns encodes clock convention in the locale's short time pattern:
  // 12-hour locales use the lowercase `h` token, 24-hour locales use `H`.
  return locale.formatLong.time({ width: "short" }).includes("h");
}

/**
 * Format the date to a string with the format yyyyMMdd
 * Example: 2024-12-06
 * @param {date} value
 */
function formatToyyyyMMdd(value) {
  return format(value, yyyyMMdd);
}

/**
 * Format the date to a string with the format DDMMM
 * Example: 06 Dec
 * @param {date} value
 */
function formatToDDMMM(value) {
  return format(value, DDMMM);
}

/**
 * Format the date to a string with the format DMMM
 * Example: 6 Dec
 * @param {date} value
 */
function formatToDMMM(value) {
  return format(value, DMMM);
}

/**
 * Format the date to a string with the format DMMMM
 * Example: 06 December
 * @param {date} value
 */
function formatToDMMMM(value) {
  return format(value, DMMMM);
}

/**
 * Format the date to a string with the format EEEEDMMMM
 * Example: Friday, 6 December
 * @param {date} value
 */
function formatToEEEEDMMMM(value) {
  return format(value, EEEEDMMMM);
}

/**
 * Format the date to a string with the format EEEEDMMMMYYYY
 * Example: Friday, 6 December 2024
 * @param {date} value
 */
function formatToEEEEDMMMMYYYY(value) {
  return format(value, EEEEDMMMMYYYY);
}

/**
 * Format the date to a string with the format DDMMMYYYY
 * Example: 06 Dec 2024
 * @param {date} value
 */
function formatToDDMMMYYYY(value) {
  return format(value, DDMMMYYYY);
}

/**
 * Format the date to a string with the format DMMMMYYYY
 * Example: 6 December 2024
 * @param {date} value
 */
function formatToDMMMMYYYY(value) {
  return format(value, DMMMMYYYY);
}

/**
 * Format the date to a string with the format DDMMMYYYYHHmmA or DDMMMYYYYHHmm
 * Examples:
 *  - use12hIfAvailable = true => 06 Dec 2024 10:25 PM
 *  - use12hIfAvailable = false => 06 Dec 2024 22:25
 * @param {date} value
 */
function formatToDDMMMYYYYHmm(value, use12hIfAvailable = true) {
  return format(
    value,
    use12HourFormat() && use12hIfAvailable ? DDMMMYYYYHHmmA : DDMMMYYYYHHmm
  );
}

/**
 * Format the date to a string with the format DDMMMYYYYHHMMssA or DDMMMYYYYHHMMss
 * Examples:
 *  - use12hIfAvailable = true => 06 Dec 2024 10:25:06 PM
 *  - use12hIfAvailable = false => 06 Dec 2024 22:25:06
 * @param {date} value
 */
function formatToDDMMMYYYYHHMMss(value, use12hIfAvailable = true) {
  return format(
    value,
    use12HourFormat() && use12hIfAvailable ? DDMMMYYYYHHMMssA : DDMMMYYYYHHMMss
  );
}

/**
 * Format the date to a string with the format DDMMMYYYYHHmmssSSSA or DDMMMYYYYHHmmssSSS
 * Examples:
 *  - use12hIfAvailable = true => 06 Dec 2024 10:25:06.930 PM
 *  - use12hIfAvailable = false => 06 Dec 2024 22:25:06.930
 * @param {date} value
 */
function formatToDDMMMYYYYHHmmssSSS(value, use12hIfAvailable = true) {
  return format(
    value,
    use12HourFormat() && use12hIfAvailable
      ? DDMMMYYYYHHmmssSSSA
      : DDMMMYYYYHHmmssSSS
  );
}

/**
 * Format the date to a string with the format HmmA or Hmm
 * Examples:
 *  - use12hIfAvailable = true => 8:25 PM
 *  - use12hIfAvailable = false => 20:25
 * @param {date} value
 */
function formatToHmm(value, use12hIfAvailable = true) {
  return format(value, use12HourFormat() && use12hIfAvailable ? HmmA : Hmm);
}

/**
 * Format the date to a string with the format HHmmA or HHmm
 * Examples:
 *  - use12hIfAvailable = true => 08:25 PM
 *  - use12hIfAvailable = false => 20:25
 * @param {date} value
 */
function formatToHHmm(value, use12hIfAvailable = true) {
  return format(value, use12HourFormat() && use12hIfAvailable ? HHmmA : HHmm);
}

/**
 * Format the date to a string with the format MMDD
 * Example: 12 06
 * @param {date} value
 */
function formatToMMDD(value) {
  return format(value, MMDD);
}

/**
 * Format the date to a string with the format MMMMYYYY
 * Example: December 2024
 * @param {date} value
 */
function formatToMMMMYYYY(value) {
  return format(value, MMMMYYYY);
}

/**
 * Returns whether the provided object is a valid date
 * @param date
 */
function isValidDate(date) {
  return date instanceof Date && !isNaN(date);
}

// Returns whether the current date is today
function isToday(date) {
  let today = new Date();
  return isSameDay(today, date);
}

function isYesterday(date) {
  let yesterday = subDays(new Date(), 1);
  return isSameDay(yesterday, date);
}

function isSameYearAsToday(date) {
  let today = new Date();
  return isSameYear(today, date);
}

// Returns whether the date is in the past
function isInThePast(date) {
  return date < new Date();
}

// Get the minimum time of the day for the given date
function getDayDate(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
}

function startOfWeekForCurrentLanguage(date) {
  return startOfWeek(date, {
    locale: locales[store.getters["Language/language"]],
  });
}

function endOfWeekForCurrentLanguage(date) {
  return endOfWeek(date, {
    locale: locales[store.getters["Language/language"]],
  });
}

function getWeekForCurrentLanguage(date) {
  return getWeek(date, {
    locale: locales[store.getters["Language/language"]],
  });
}

/**
 * Converts "from" and "to" dates to strings and removes month on "from" date if same month
 * @param {*} fromDate
 * @param {*} toDate
 * @returns ["17", "23 Feb"] or ["24 Feb", "2 Mar"]
 */
function formatLabelKeepLastMonth(fromDate, toDate) {
  const isSameMonth = fromDate.getMonth() === toDate.getMonth();
  const firstDate = isSameMonth ? format(fromDate, D) : formatToDMMM(fromDate);
  const lastDate = formatToDMMM(toDate);
  return [firstDate, lastDate];
}

/**
 * Converts "from" and "to" dates to strings and removes month on "to" date if same month
 * @param {*} fromDate
 * @param {*} toDate
 * @returns ["17 Feb", "23"] or ["24 Feb", "2 Mar"]
 */
function formatLabelKeepFirstMonth(fromDate, toDate) {
  const isSameMonth = fromDate.getMonth() === toDate.getMonth();
  const firstDate = formatToDMMM(fromDate);
  const lastDate = isSameMonth ? format(toDate, D) : formatToDMMM(toDate);
  return [firstDate, lastDate];
}

/**
 * Format and return the date range as a string
 * Examples: "17 - 23 Feb", "24 Feb - 2 Mar"
 * @param {date} fromDate
 * @param {date} toDate
 * @returns {string}
 */
function getDateRangeLabel(fromDate, toDate) {
  const isRTL = store.getters["Language/isRTLLanguage"];
  const isJapanese = store.getters["Language/language"] === locales.ja.code;

  const [firstDate, lastDate] = isJapanese
    ? formatLabelKeepFirstMonth(fromDate, toDate)
    : formatLabelKeepLastMonth(fromDate, toDate);

  const label = isRTL
    ? `\u200F${lastDate}\u200E - \u200F${firstDate}`
    : `${firstDate} - ${lastDate}`;

  return label;
}

export {
  getDefaultBirthDate,
  formatToyyyyMMdd,
  formatToDDMMMYYYYHHMMss,
  formatToDDMMMYYYYHHmmssSSS,
  formatToDDMMMYYYY,
  formatToDMMMMYYYY,
  formatToDDMMM,
  formatToDMMMM,
  formatToDMMM,
  formatToMMMMYYYY,
  formatToDDMMMYYYYHmm,
  formatToEEEEDMMMM,
  formatToEEEEDMMMMYYYY,
  formatToHmm,
  formatToHHmm,
  formatToMMDD,
  format,
  isValidDate,
  isToday,
  isYesterday,
  getDayDate,
  isInThePast,
  isSameYearAsToday,
  use12HourFormat,
  startOfWeekForCurrentLanguage,
  endOfWeekForCurrentLanguage,
  getWeekForCurrentLanguage,
  getDateRangeLabel,
};
